import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { openRemoteChannel, type RemoteChannelHandle } from '../services/presentationRemoteChannel';
import {
  type AnyRemoteEnvelope,
  type RemoteAckError,
  type RemotePayloadMap,
  type RemoteSlideNavDir,
  type RemoteConnectionStatus,
  type RemotePresenceState,
  type RemotePresentationKind,
} from '../types/presentationRemote';

import {
  getRemoteDeviceId,
  acquireRemoteSessionId,
  lowestFreeAutoNumber,
  resolvePresentationName,
  sanitizeRemoteName,
  setCustomPresentationName,
} from '../utils/presentationRemoteIdentity';
import { getActiveTtsSettings, listVoicesCompact, loadTtsSettings, saveTtsSettings } from '../utils/remoteSpeech';


export type RemoteHostCommandResult = { ok: boolean; error?: RemoteAckError };
/** 프레젠 페이지가 처리할 명령 (RENAME 제외). 반환값이 ACK로 전송됨 */
export type RemoteHostCommandHandler = (msg: AnyRemoteEnvelope) => Promise<RemoteHostCommandResult> | RemoteHostCommandResult;

/**
 * 메인 프레젠테이션 탭 — 리모컨 Presence 등록.
 * 세션이 없거나 Realtime 실패 시 아무 것도 하지 않는다(기존 동작 무영향).
 */
/**
 * Presence는 정적·최소(role, sessionId, deviceId, name, autoNumber, since)이며
 * 참가/재연결 시와 원격 이름 변경 시에만 전송. 음악 상태 등은 보고하지 않음(명령별 ACK로 결과 전달).
 */
export function usePresentationRemoteHost(params: {
  onCommand?: RemoteHostCommandHandler;
  /** false면 Supabase Realtime 연결 자체를 열지 않음(같은 브라우저 BroadcastChannel만) */
  remoteEnabled?: boolean;
  /** 'lesson' = 수업 프레젠: 로컬(BroadcastChannel) 전용 — 공급자와 무관하게 온라인 등록 안 함 */
  presentationKind?: RemotePresentationKind;
  /** 수업 프레젠 표시용 제목 */
  title?: string;
  /** false면 아무 것도 등록하지 않음 */
  enabled?: boolean;
  /** 수업 프레젠: 현재 슬라이드 상태(리모컨에 표시) — 없으면 슬라이드 제어 미지원 */
  slideState?: RemotePayloadMap['SLIDE_STATE'] | null;
  /** 수업 프레젠: 리모컨의 슬라이드 이동 요청 처리 */
  onSlideNav?: (dir: RemoteSlideNavDir, index?: number) => void;
}) {
  const { onCommand, presentationKind = 'main', title = '', enabled = true } = params;
  const isLessonKind = presentationKind === 'lesson';
  /* 수업 프레젠: 항상 로컬(같은 기기 BroadcastChannel) 전용 — 온라인 대상은 개별 디바이스 프레젠(교사당 1개 방)만 */
  const localOnly = isLessonKind;
  const remoteEnabled = isLessonKind ? false : !!params.remoteEnabled;
  const titleRef = useRef(title);
  titleRef.current = title;
  const pushStateRef = useRef<(() => void) | null>(null);
  const remoteEnabledRef = useRef(remoteEnabled);
  remoteEnabledRef.current = remoteEnabled;
  const onCommandRef = useRef<RemoteHostCommandHandler | undefined>(onCommand);
  onCommandRef.current = onCommand;
  const slideStateRef = useRef(params.slideState ?? null);
  slideStateRef.current = params.slideState ?? null;
  const onSlideNavRef = useRef(params.onSlideNav);
  onSlideNavRef.current = params.onSlideNav;
  const idsRef = useRef<{ sessionId: string; deviceId: string }>({ sessionId: '', deviceId: '' });
  const handleRef = useRef<RemoteChannelHandle | null>(null);
  const [remoteStatus, setRemoteStatus] = useState<RemoteConnectionStatus>('idle');
  const stateRef = useRef<RemotePresenceState | null>(null);

  useEffect(() => {
    let cancelled = false;
    let handle: RemoteChannelHandle | null = null;
    let sessionId = '';
    const onPageHide = () => handle?.close();
    const deviceId = getRemoteDeviceId();
    const since = Date.now();
    let autoNumber = 0;

    const pushState = (userId: string) => {
      if (!handle || autoNumber <= 0) return;
      const t = titleRef.current.trim().slice(0, 60);
      const next: RemotePresenceState = {
        role: 'presentation',
        sessionId,
        deviceId,
        name: isLessonKind
          ? `수업 프레젠${t ? ` · ${t}` : ''}`
          : resolvePresentationName(userId, deviceId, autoNumber),
        autoNumber,
        since,
        lastSeen: Date.now(),
        ...(isLessonKind ? { presentationKind: 'lesson' as const, ...(t ? { title: t } : {}) } : {}),
      };
      stateRef.current = next;
      handle.track(next);
    };

    if (!enabled) return undefined;
    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const userId = data.session?.user?.id;
        if (cancelled || !userId) return;
        sessionId = await acquireRemoteSessionId();
        if (cancelled) return;
        idsRef.current = { sessionId, deviceId };
        loadTtsSettings(userId, deviceId);
        handle = openRemoteChannel({
          userId,
          sessionId,
          remote: remoteEnabledRef.current,
          localOnly,
          onStatus: setRemoteStatus,
          onPresence: (list) => {
            /* 번호는 같은 종류끼리 — 수업 프레젠은 101번부터(메인 번호와 겹치지 않게) */
            const isLesson = (p: RemotePresenceState) => p.presentationKind === 'lesson';
            const others = list.filter((p) => p.sessionId !== sessionId && isLesson(p) === isLessonKind);
            const base = isLessonKind ? 100 : 0;
            const clash = others.some(
              (p) => p.role === 'presentation' && p.autoNumber === autoNumber && p.since <= since
            );
            if (autoNumber <= 0 || clash) {
              autoNumber =
                base +
                lowestFreeAutoNumber(
                  others.map((p) => ({ ...p, autoNumber: (p.autoNumber ?? 0) - base })),
                  sessionId
                );
              pushState(userId);
            }
          },
          onMessage: (msg) => {
            if (msg.type === 'PRESENCE_PING') {
              if (autoNumber > 0) void handle?.send('PRESENCE_PONG', { at: Date.now() }, { targetSessionId: msg.fromSessionId });
              return;
            }
            if (
              msg.type === 'PRESENCE_PONG' ||
              msg.type === 'COMMAND_ACK' ||
              msg.type === 'SUBMIT_ANSWER' ||
              msg.type === 'PRESENTATION_STATUS' ||
              msg.type === 'TTS_VOICES' ||
              msg.type === 'SLIDE_STATE'
            )
              return;
            if (msg.targetSessionId !== sessionId) return;
            if (msg.type === 'SLIDE_NAV') {
              const nav = onSlideNavRef.current;
              const dir = msg.payload?.dir;
              const okDir = dir === 'prev' || dir === 'next' || dir === 'first' || dir === 'last' || dir === 'goto';
              if (nav && okDir) {
                const idx = Number(msg.payload?.index);
                nav(dir, Number.isFinite(idx) ? Math.floor(idx) : undefined);
              }
              /* 이동 결과는 상태 변경 effect 가 전체에 알림. sync(또는 이동 없음)는 요청자에게 바로 응답 */
              const st = slideStateRef.current;
              if (st && (!okDir || !nav)) void handle?.send('SLIDE_STATE', st, { targetSessionId: msg.fromSessionId });
              return;
            }
            if (msg.type === 'GET_TTS_VOICES' || msg.type === 'SET_TTS_SETTINGS') {
              const h = handle;
              const includeAll = msg.type === 'GET_TTS_VOICES' ? !!msg.payload?.includeAll : false;
              if (msg.type === 'SET_TTS_SETTINGS') saveTtsSettings(userId, deviceId, msg.payload?.settings ?? {});
              void listVoicesCompact(includeAll)
                .then((list) => {
                  h?.ack(msg, true);
                  void h?.send(
                    'TTS_VOICES',
                    { ...list, includeAll, settings: getActiveTtsSettings() },
                    { targetSessionId: msg.fromSessionId }
                  );
                })
                .catch(() => h?.ack(msg, false, 'tts_unavailable'));
              return;
            }
            if (msg.type === 'RENAME_PRESENTATION') {
              if (autoNumber <= 0 || isLessonKind) {
                handle?.ack(msg, false, 'unknown');
                return;
              }
              setCustomPresentationName(userId, deviceId, autoNumber, sanitizeRemoteName(msg.payload.name));
              pushState(userId);
              handle?.ack(msg, true);
              return;
            }
            const h = handle;
            const fn = onCommandRef.current;
            if (!fn) {
              h?.ack(msg, false, 'not_implemented');
              return;
            }
            void Promise.resolve()
              .then(() => fn(msg))
              .then((r) => h?.ack(msg, !!r?.ok, r?.ok ? undefined : r?.error ?? 'unknown'))
              .catch(() => h?.ack(msg, false, 'unknown'));
          },
        });
        handleRef.current = handle;
        pushStateRef.current = () => pushState(userId);
        if (cancelled) {
          handle.close();
          return;
        }
        window.addEventListener('pagehide', onPageHide);
      } catch {
        /* 조용히 무시 */
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      handle?.close();
      handleRef.current = null;
      pushStateRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, localOnly]);

  /* 수업 제목이 바뀌면 이름 갱신 */
  useEffect(() => {
    if (isLessonKind) pushStateRef.current?.();
  }, [title, isLessonKind]);


  /* 허용 스위치/이용권 변경 시 Supabase 연결 on/off (off면 untrack→removeChannel, 채널이 없으면 소켓도 종료) */
  useEffect(() => {
    handleRef.current?.setRemoteEnabled(remoteEnabled);
  }, [remoteEnabled]);

  /* 슬라이드 상태 변경 → 리모컨들에 알림 (변경 시에만 1회) */
  const slideIdx = params.slideState?.index;
  const slideTotal = params.slideState?.total;
  const slideLabel = params.slideState?.label;
  useEffect(() => {
    if (slideIdx == null || slideTotal == null) return;
    void handleRef.current?.send('SLIDE_STATE', { index: slideIdx, total: slideTotal, ...(slideLabel ? { label: slideLabel } : {}) });
  }, [slideIdx, slideTotal, slideLabel]);

  /** 질문에 대한 응답 전송 (질문 보낸 리모컨에게만) */
  const submitAnswer = useCallback(
    async (
      requestId: string,
      toSessionId: string,
      content: string,
      student?: { studentId: string; studentLabel: string }
    ): Promise<boolean> => {
      const h = handleRef.current;
      const { sessionId, deviceId } = idsRef.current;
      if (!h || !sessionId) return false;
      const id = await h.send(
        'SUBMIT_ANSWER',
        {
          requestId,
          sessionId,
          deviceId,
          content: content.slice(0, 500),
          answeredAt: Date.now(),
          ...(student ? { studentId: student.studentId.slice(0, 64), studentLabel: student.studentLabel.slice(0, 40) } : {}),
        },
        { targetSessionId: toSessionId }
      );
      return !!id;
    },
    []
  );

  const reconnect = useCallback(() => handleRef.current?.reconnect(), []);

  return { submitAnswer, remoteStatus, reconnect };
}
